const CONFIG = {
  templateSettings: {
    headerWrapperSelector: '.header-wrapper',
    darkThemeClass: 'nav-dark'
  },
  megaMenu: {
    menuSelector: '.mega-menu',
    headerWrapperSelector: '.header-wrapper',
    openClass: 'nav-open'
  },
  membership: {
    containerSelector: '.membership-option',
    addToCartButtonClass: 'membership-add-to-cart',
    cartDrawerSelector: 'cart-drawer',
    loadingClass: 'is-loading',
    productSelectClass: 'membership-product-select',
    productOptionClass: 'membership-product-option',
    activeOptionClass: 'option-active'    
  },
  countUp: {
    headingSelector: '.column-heading[data-count-up="true"]',
    duration: 1500, // Animation duration in milliseconds
    threshold: 0.1 // Intersection observer threshold
  }  
};

// Add to existing logDebug function if not already present
const logDebug = (label, data) => console.log(`[DEBUG] ${label}:`, data);

// Nav Dark
class TemplateSettingsHandler {
  constructor() {
    this.headerWrapper = document.querySelector(CONFIG.templateSettings.headerWrapperSelector);
    this.init();
  }

  init() {
    if (!this.headerWrapper) {
      return;
    }

    // Look for template settings section or inline data attribute
    const templateSection = document.querySelector('template-settings');
    const pageWrapper = document.querySelector('[data-dark-header]');
    
    // Get dark header setting from either source
    const darkHeader = templateSection?.dataset.darkHeader === 'true' || 
                      pageWrapper?.dataset.darkHeader === 'true';

    // Apply dark theme if enabled
    if (darkHeader) {
      this.headerWrapper.classList.add(CONFIG.templateSettings.darkThemeClass);
    }
  }
}

// Mega Menu Manager Class
class MegaMenuManager {
  constructor() {
    this.megaMenu = document.querySelector(CONFIG.megaMenu.menuSelector);
    this.headerWrapper = document.querySelector(CONFIG.megaMenu.headerWrapperSelector);
    this.init();
  }

  init() {
    if (!this.megaMenu || !this.headerWrapper) {
      return;
    }

    this.addEventListeners();
    this.checkInitialState();
  }

  addEventListeners() {
    this.megaMenu.addEventListener('toggle', () => this.handleMenuToggle());
  }

  handleMenuToggle() {
    const isMenuOpen = this.megaMenu.hasAttribute('open');
    this.headerWrapper.classList.toggle(CONFIG.megaMenu.openClass, isMenuOpen);
  }

  checkInitialState() {
    this.handleMenuToggle();
  }

  cleanup() {
    this.megaMenu.removeEventListener('toggle', () => this.handleMenuToggle());
  }
}

// Count Up Animation Manager
// Count Up Animation Manager
class CountUpManager {
  constructor() {
    this.init();
  }

  init() {
    // Find all column headings with count-up enabled
    this.countUpHeadings = document.querySelectorAll(CONFIG.countUp.headingSelector);
    
    if (this.countUpHeadings.length === 0) {
      return;
    }
    
    // Store original text values for all headings
    this.countUpHeadings.forEach(heading => {
      // Store the original text to reference later if not already stored
      if (!heading.hasAttribute('data-original-text')) {
        heading.setAttribute('data-original-text', heading.textContent);
      }
    });
    
    // Options for the Intersection Observer
    const options = {
      root: null, // viewport
      rootMargin: '0px',
      threshold: CONFIG.countUp.threshold
    };
    
    // Create an observer that triggers animations when elements enter viewport
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Start count-up animation when element enters viewport
          this.animateCountUp(entry.target);
          
          // Keep observing to re-animate when it comes back into view
          // (Not unobserving the element)
        } else {
          // When element leaves viewport, reset to original state for next animation
          const originalText = entry.target.getAttribute('data-original-text');
          if (originalText) {
            entry.target.textContent = originalText;
          }
        }
      });
    }, options);
    
    // Observe each heading
    this.countUpHeadings.forEach(heading => {
      this.observer.observe(heading);
    });
  }
  
  animateCountUp(headingElement) {
    const originalText = headingElement.getAttribute('data-original-text');
    if (!originalText) return;
    
    const duration = CONFIG.countUp.duration;
    const frameDuration = 1000 / 60; // 60fps
    const totalFrames = Math.round(duration / frameDuration);
    
    // Find all numbers in the text with regex
    // This regex matches numbers with optional commas as thousands separators
    const regex = /(\d{1,3}(,\d{3})*(\.\d+)?|\d+(\.\d+)?)/g;
    let match;
    const numbers = [];
    
    // Create a working copy of the text for the regex
    let textCopy = originalText;
    
    // Extract all numbers and their positions
    while ((match = regex.exec(textCopy)) !== null) {
      const numStr = match[0];
      const pos = match.index;
      // Convert the string to a number, removing commas
      const num = parseFloat(numStr.replace(/,/g, ''));
      
      numbers.push({
        originalStr: numStr,
        value: num,
        start: 0,
        pos: pos,
        length: numStr.length,
        hasCommas: numStr.includes(',')
      });
    }
    
    // If no numbers found, exit
    if (numbers.length === 0) return;
    
    let frame = 0;
    let animationId = null;
    
    // Store the animation ID on the element to allow cancellation if needed
    if (headingElement.dataset.animationId) {
      cancelAnimationFrame(parseInt(headingElement.dataset.animationId));
    }
    
    // Animation function
    const updateNumbers = () => {
      // Calculate progress (0 to 1)
      const progress = frame / totalFrames;
      // Easing function for smoother animation
      const easedProgress = this.easeOutQuad(progress);
      
      // Create a copy of the original text
      let newText = originalText;
      let offset = 0;
      
      // Update each number in the text
      numbers.forEach(num => {
        // Calculate current value based on progress
        const currentValue = Math.round(num.value * easedProgress);
        
        // Format the number with commas if the original had them
        let formattedValue = currentValue.toString();
        if (num.hasCommas) {
          formattedValue = this.addCommas(formattedValue);
        }
        
        // Replace the original number in the text
        const startPos = num.pos + offset;
        const oldLength = num.currentStr ? num.currentStr.length : 0;
        newText = newText.substring(0, startPos) + 
                  formattedValue + 
                  newText.substring(startPos + oldLength);
        
        // Update the offset for subsequent replacements
        offset += formattedValue.length - oldLength;
        
        // Store the current string for the next iteration
        num.currentStr = formattedValue;
      });
      
      // Update the heading text
      headingElement.textContent = newText;
      
      // Continue animation until complete
      if (frame < totalFrames) {
        frame++;
        animationId = requestAnimationFrame(updateNumbers);
        headingElement.dataset.animationId = animationId;
      } else {
        // Animation complete, clear the ID
        delete headingElement.dataset.animationId;
      }
    };
    
    // Start the animation
    animationId = requestAnimationFrame(updateNumbers);
    headingElement.dataset.animationId = animationId;
  }
  
  easeOutQuad(t) {
    return t * (2 - t);
  }
  
  addCommas(numStr) {
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
}

// Initialize Managers
document.addEventListener('DOMContentLoaded', () => {
  new MegaMenuManager();
});

// Make Product Columns Equal Height
CONFIG.productHeights = {
  containerSelector: '.flexible-product',
  elements: [
    {
      selector: '.flexible-product-name',
      description: 'Product names'
    },
    {
      selector: '.flexible-product-swatches',
      description: 'Product Swatches'
    },
    {
      selector: '.flexible-product-text',
      description: 'Product descriptions'
    },
    {
      selector: '.flexible-product-price',
      description: 'Product prices'
    },
    {
      selector: '.flexible-product-button',
      description: 'Product buttons'
    },
    {
      selector: '.flexible-product-icon-1',
      description: 'Icon wrapper 1'
    },
    {
      selector: '.flexible-product-icon-2',
      description: 'Icon wrapper 2'
    },
    {
      selector: '.flexible-product-icon-3',
      description: 'Icon wrapper 3'
    },
    {
      selector: '.flexible-product-icon-4',
      description: 'Icon wrapper 4'
    }
  ]
};

class ProductHeightsManager {
  constructor(initializationPromise = null) {
    this.initializationPromise = initializationPromise;
    this.init();
    this.bindEvents();
  }

  async init() {
    if (this.initializationPromise) {
      await this.initializationPromise;
    }
    
    this.setEqualHeights();
  }

  bindEvents() {
    // Reset heights on window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.resetHeights();
        this.setEqualHeights();
      }, 250);
    });

    // Reset heights when elements become visible
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.resetHeights();
          this.setEqualHeights();
        }
      });

      document.querySelectorAll(CONFIG.productHeights.containerSelector).forEach(container => {
        observer.observe(container);
      });
    }
  }

  getMaxHeight(elements) {
    let maxHeight = 0;
    elements.forEach(element => {
      // Reset height to get natural height
      element.style.height = 'auto';
      const height = element.offsetHeight;
      maxHeight = Math.max(maxHeight, height);
    });
    return maxHeight;
  }

  resetHeights() {
    CONFIG.productHeights.elements.forEach(({ selector }) => {
      document.querySelectorAll(selector).forEach(element => {
        element.style.height = 'auto';
      });
    });
  }

  setEqualHeights() {
    CONFIG.productHeights.elements.forEach(({ selector, description }) => {
      const elements = document.querySelectorAll(selector);
      
      if (elements.length > 1) {
        const maxHeight = this.getMaxHeight(elements);
        elements.forEach(element => {
          element.style.height = `${maxHeight}px`;
        });
      }
    });
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  new ProductHeightsManager();
});



// Product Column Swatches - Click Handler with Custom Discount Price
CONFIG.productSwatches = {
  swatchContainerSelector: '.flexible-product-swatches',
  activeSwatchClass: 'swatch-active',
  imageSelector: '.column-image',
  priceSelector: '.flexible-product-price',
  moneyFormat: window.theme?.moneyFormat || "${{amount}}"
};

class ProductSwatchesManager {
  constructor() {
    this.init();
  }

  init() {
    this.productContainers = document.querySelectorAll('.flexible-product');
    if (!this.productContainers.length) {
      return;
    }

    this.addSwatchClickHandlers();
  }

  addSwatchClickHandlers() {
    // Find all swatches and add click handlers
    document.querySelectorAll('.variant-swatch').forEach(swatch => {
      swatch.addEventListener('click', () => {
        const container = swatch.closest('.flexible-product');
        const variantId = swatch.dataset.variantId;
        
        if (!container || !variantId) return;
        
        // Update active state for all swatches in this container
        container.querySelectorAll('.variant-swatch').forEach(s => 
          s.classList.remove(CONFIG.productSwatches.activeSwatchClass)
        );
        swatch.classList.add(CONFIG.productSwatches.activeSwatchClass);
        
        // Update product image and price based on selected variant
        this.updateProductDisplay(container, variantId);
      });
    });
  }
  
  async updateProductDisplay(container, variantId) {
    // Get product handle from container
    const productHandle = container.dataset.product;
    if (!productHandle) return;
    
    try {
      // Fetch product data
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) throw new Error('Product fetch failed');
      const product = await response.json();
      
      // Find the selected variant
      const variant = product.variants.find(v => v.id.toString() === variantId);
      if (!variant) return;
      
      const productImage = container.querySelector(CONFIG.productSwatches.imageSelector);
      if (!productImage) return;
      
      // Try to use variant metafield image first
      const variantIndex = container.dataset.variantIds?.split(',').indexOf(variantId);
      if (variantIndex !== -1) {
        const variantImageUrls = container.dataset.variantMainImages?.split(',') || [];
        const imageUrl = variantImageUrls[variantIndex];
        
        if (imageUrl && imageUrl.trim() !== '') {
          // Extract the base URL without Shopify image parameters
          const baseUrl = imageUrl.split('?')[0];
          // Apply the width parameter consistent with initial load
          productImage.src = `${baseUrl}?width=800`;
          productImage.srcset = ''; // Reset srcset if any
        } 
        // If no valid metafield image, use variant featured image as fallback
        else if (variant.featured_image) {
          productImage.src = variant.featured_image.src;
          productImage.srcset = ''; // Reset srcset if any
        }
        // If neither metafield nor variant image, keep current image
      }
      // If no variant index found, still try to use variant featured image
      else if (variant.featured_image) {
        productImage.src = variant.featured_image.src;
        productImage.srcset = ''; // Reset srcset if any
      }
      
      // Update price using discount price if available
      this.updatePriceWithDiscount(container, variant);
    } catch (error) {
      logDebug('Error updating product display:', error);
    }
  }
  
  updatePriceWithDiscount(container, variant) {
    const priceContainer = container.querySelector(CONFIG.productSwatches.priceSelector);
    if (!priceContainer) return;
    
    // Get variant discount price from data attribute
    const variantIds = container.dataset.variantIds?.split(',') || [];
    const discountPrices = container.dataset.variantDiscountPrices?.split(',') || [];
    
    const variantIndex = variantIds.indexOf(variant.id.toString());
    const discountPrice = discountPrices[variantIndex] || '';
    
    if (discountPrice && discountPrice !== '') {
      // Show discount price
      const parsedDiscountPrice = parseFloat(discountPrice);
      const comparePrice = `<span class="compare-price line-through text-gray-600">${this.formatMoney(variant.price)}</span> `;
      const salePrice = `<span class="regular-price on-sale">${this.formatMoney(parsedDiscountPrice * 100)}</span>`;
      
      priceContainer.innerHTML = comparePrice + salePrice;
    } else {
      // Show regular price
      priceContainer.innerHTML = `<span class="regular-price">${this.formatMoney(variant.price)}</span>`;
    }
  }

  formatMoney(cents) {
    let formattedPrice = '';
    
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      // Use Shopify's built-in formatMoney which respects locale settings
      formattedPrice = Shopify.formatMoney(cents, CONFIG.productSwatches.moneyFormat);
    } else {
      // Fallback to browser's Intl formatter
      // Get current Shopify currency or fallback to USD
      const currentCurrency = Shopify?.currency?.active || 'USD';
      
      formattedPrice = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currentCurrency
      }).format(cents/100);
    }
    
    // Remove cents if they're .00
    formattedPrice = formattedPrice.replace(/\.00($|\s)/, '$1');
    
    // Handle specific currency formats based on currency code
    const currentCurrency = Shopify?.currency?.active || 'USD';
    
    // Special formatting for CAD - ensure it shows as "$XXX CAD" not "CA$XXX"
    if (currentCurrency === 'CAD' && !formattedPrice.includes('CAD')) {
      formattedPrice = formattedPrice.replace(/^CA\$/, '$');
      formattedPrice += ' CAD';
    }
    
    // Special formatting for GBP - ensure it shows as "£XXX GBP" not "£XXX"
    if (currentCurrency === 'GBP' && !formattedPrice.includes('GBP')) {
      formattedPrice += ' GBP';
    }
    
    return formattedPrice;
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  new ProductSwatchesManager();
});

//Membership ATC
class MembershipCartManager {
  constructor() {
    this.cartDrawer = document.querySelector(CONFIG.membership.cartDrawerSelector);
    this.isDrawerCart = Boolean(this.cartDrawer);
    this.init();
  }

  init() {
    this.containers = document.querySelectorAll(CONFIG.membership.containerSelector);
    
    if (!this.containers.length) {
      return;
    }

    this.addEventListeners();
  }

  addEventListeners() {
    // Add click handlers for product options
    document.querySelectorAll(`.${CONFIG.membership.productOptionClass}`).forEach(option => {
      option.addEventListener('click', () => this.handleOptionSelect(option));
    });

    // Add click handlers for add to cart button
    document.querySelectorAll(`.${CONFIG.membership.addToCartButtonClass}`).forEach(button => {
      button.addEventListener('click', (e) => this.handleAddToCart(e));
    });
  }

  handleOptionSelect(option) {
    // Remove active class from all options in the container
    const container = option.closest(CONFIG.membership.containerSelector);
    container.querySelectorAll(`.${CONFIG.membership.productOptionClass}`).forEach(opt => {
      opt.classList.remove(CONFIG.membership.activeOptionClass);
    });

    // Add active class to selected option
    option.classList.add(CONFIG.membership.activeOptionClass);
  }

  getSelectedProduct(container) {
    const selectedOption = container.querySelector(`.${CONFIG.membership.productOptionClass}.${CONFIG.membership.activeOptionClass}`);
    if (!selectedOption) {
      return null;
    }
  
    const productData = {
      id: parseInt(selectedOption.dataset.productId),
      selling_plan: parseInt(selectedOption.dataset.sellingPlan)
    };
    
    return productData;
  }

  async handleAddToCart(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const container = button.closest(CONFIG.membership.containerSelector);
    
    // Get selected product data
    const selectedProduct = this.getSelectedProduct(container);
    if (!selectedProduct) {
      return;
    }
  
    // Show loading state
    const originalText = button.textContent;
    button.classList.add(CONFIG.membership.loadingClass);
    button.textContent = 'Adding...';
    button.disabled = true;
  
    try {
      const items = [{
        id: selectedProduct.id,
        quantity: 1,
        selling_plan: selectedProduct.selling_plan
      }];
  
      // Add to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items }),
      });
  
      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(`Failed to add to cart: ${responseText}`);
      }
  
      const result = JSON.parse(responseText);
  
      // Instead of opening drawer, redirect to cart
      window.location.href = '/cart';
  
    } catch (error) {
      button.textContent = 'Error - Try Again';
      setTimeout(() => {
        button.textContent = originalText;
      }, 3000);
    } finally {
      button.classList.remove(CONFIG.membership.loadingClass);
      button.disabled = false;
      if (button.textContent === 'Adding...') {
        button.textContent = originalText;
      }
    }
  }

  async updateCart() {
    try {
      // Get updated cart data
      const cartDataResponse = await fetch('/cart.js');
      const cartData = await cartDataResponse.json();
      
      // Update cart count
      this.updateCartCount(cartData.item_count);

      // Open cart drawer if it exists
      if (this.isDrawerCart && this.cartDrawer?.open) {
        // Update drawer content first
        const drawerResponse = await fetch('?section_id=cart-drawer');
        if (drawerResponse.ok) {
          const html = await drawerResponse.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newContent = doc.querySelector('.drawer__inner');
          
          if (newContent) {
            const currentContent = this.cartDrawer.querySelector('.drawer__inner');
            if (currentContent) {
              currentContent.innerHTML = newContent.innerHTML;
            }
          }
        }

        // Remove empty class and open drawer
        this.cartDrawer.classList.remove('is-empty');
        setTimeout(() => this.cartDrawer.open(), 100);
      }

      // Trigger cart update events
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cartData }
      }));

      document.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: cartData
      }));

    } catch (error) {
      logDebug('Error updating cart:', error);
    }
  }

  updateCartCount(count) {
    const cartCount = document.querySelector('.cart-count-bubble');
    if (cartCount) {
      const countSpan = cartCount.querySelector('span');
      if (countSpan) {
        countSpan.textContent = count;
        cartCount.style.display = count > 0 ? 'flex' : 'none';
      }
    }
  }
}

// First, add this to your CONFIG object at the top of the file:
CONFIG.modal = {
  modalSelector: '#global-modal',
  modalWrapperClass: 'modal-wrap',
  modalContentClass: 'modal-content',
  modalCloseClass: 'modal-close',
  triggerAttribute: 'data-modal-trigger',
  contentAttribute: 'data-modal-content',
  activeClass: 'is-active'
};

// Updated ModalManager class
class ModalManager {
  constructor() {
    this.modal = document.querySelector(CONFIG.modal.modalSelector);
    this.isInitialized = false;
    this.activeVideo = null;
    this.init();
  }

  init() {
    if (!this.modal) {
      return;
    }

    this.modalContent = this.modal.querySelector(`.${CONFIG.modal.modalContentClass}`);
    this.closeButton = this.modal.querySelector(`.${CONFIG.modal.modalCloseClass}`);
    this.contentWrap = this.modal.querySelector('.modal-content-wrap');
    
    if (!this.modalContent || !this.closeButton || !this.contentWrap) {
      return;
    }

    this.bindEvents();
    this.isInitialized = true;
    
    // Make the instance available globally for direct access
    window.modalManager = this;
  }

  bindEvents() {
    // Close button click
    this.closeButton.addEventListener('click', () => this.hideModal());
    
    // Background click
    this.modal.addEventListener('click', (e) => {
      if (e.target.classList.contains(CONFIG.modal.modalWrapperClass)) {
        this.hideModal();
      }
    });

    // Escape key press
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible()) {
        this.hideModal();
      }
    });

    // Delegate modal triggers
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest(`[${CONFIG.modal.triggerAttribute}]`);
      if (trigger) {
        const content = trigger.getAttribute(CONFIG.modal.contentAttribute);
        if (content) {
          this.showModal(content);
        } else {
          logDebug('ModalManager', 'No content found for modal trigger');
        }
      }
    });
    
    // Handle video buttons - use event delegation
    this.initVideoButtons();
  }
  
  initVideoButtons() {    
    // Use event delegation for all video button clicks
    document.body.addEventListener('click', (e) => {
      // Find the button or any of its children that was clicked
      const videoButton = e.target.closest('.button[data-video]');
      if (videoButton && videoButton.dataset.video) {
        e.preventDefault();
        e.stopPropagation();
        
        const videoUrl = videoButton.dataset.video;
        
        this.showVideoModal(videoUrl);
        return false;
      }
    });
  }
  
  showVideoModal(videoUrl) {
    if (!videoUrl) {
      return;
    }
        
    // Add has-video class BEFORE content is added and modal is shown
    if (this.contentWrap) {
      this.contentWrap.classList.add('has-video');
    }
    
    // Create video HTML for the modal
    const videoHTML = `
      <div class="video-container" style="width: 100%; max-width: 1200px; position: relative; padding-top: 56.25%;">
        <video 
          id="modal-video" 
          controls 
          autoplay 
          style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
          src="${videoUrl}">
        </video>
      </div>
    `;
    
    this.showModal(videoHTML);
    
    // Store reference to the video element
    this.activeVideo = this.modalContent.querySelector('video');
    
    // Ensure video plays with sound
    if (this.activeVideo) {
      this.activeVideo.muted = false;
      
      // Play video after a small delay to ensure modal is fully rendered
      setTimeout(() => {
        this.activeVideo.play()
          .then(() => logDebug('Video started playing'))
          .catch(err => logDebug('Video autoplay error:', err));
      }, 100);
    }
  }

  isVisible() {
    return this.modal.style.display === 'flex';
  }

  showModal(content) {
    // Store the current scroll position before showing the modal
    this.scrollPosition = window.pageYOffset;
    
    this.modalContent.innerHTML = content;
    this.modal.style.display = 'flex';
  
    // Add class to disable scrolling, but with fixed positioning
    if (document.body) {
      document.body.classList.add('modal-open');
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
    } else {
      console.error('ModalManager', 'document.body not found!');
    }
  
    // Force browser reflow to ensure transitions work
    this.modal.offsetHeight;
    this.modal.classList.add(CONFIG.modal.activeClass);
  }
  
  hideModal() {
    this.modal.classList.remove(CONFIG.modal.activeClass);
    
    // Stop video if playing
    if (this.activeVideo) {
      try {
        this.activeVideo.pause();
        this.activeVideo.currentTime = 0;
        this.activeVideo.src = '';
      } catch (e) {
        logDebug('ModalManager', 'Error stopping video:', e);
      }
      this.activeVideo = null;
    }
    
    // Wait for transition to complete before hiding and cleaning up
    setTimeout(() => {
      this.modal.style.display = 'none';
      this.modalContent.innerHTML = '';
  
      // Only remove has-video class after the transition completes
      if (this.contentWrap) {
        this.contentWrap.classList.remove('has-video');
      }
      
      // Re-enable scrolling and restore scroll position
      document.body.classList.remove('modal-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, this.scrollPosition);
    }, 300); // Match this to your CSS transition duration
  }
  
}

// Then in your DOMContentLoaded event listener, add:
document.addEventListener('DOMContentLoaded', () => {
  const header = document.querySelector('.header-wrapper');
  let lastScroll = window.pageYOffset;
  
  const handleScroll = () => {
    // Only apply sticky behavior on desktop (900px and above)
    if (window.innerWidth >= 900) {
      const currentScroll = window.pageYOffset;
      // Only act if we've scrolled a little (e.g., more than 50px)
      if (Math.abs(currentScroll - lastScroll) > 50) {
        if (currentScroll < lastScroll) {
          // Scrolling up: show header
          header.classList.add('header--visible');
        } else {
          // Scrolling down: hide header
          header.classList.remove('header--visible');
        }
        if (currentScroll > lastScroll && currentScroll > 100) {
          header.classList.add('header--hidden');
        } else {
          // Scrolling up: show header
          header.classList.remove('header--hidden');
        }      
        lastScroll = currentScroll;
      }
    } else {
      // Remove classes on mobile to ensure header behaves normally
      header.classList.remove('header--visible', 'header--hidden');
    }
  };

  // Add scroll event listener
  window.addEventListener("scroll", handleScroll);
  
  // Also check on resize in case window crosses breakpoint
  window.addEventListener("resize", handleScroll);
  
  // Initialize other managers...
  new TemplateSettingsHandler();
  new MegaMenuManager();
  new CountUpManager(); // Initialize the Count Up Manager
  const swatchManager = new ProductSwatchesManager();
  new ProductHeightsManager(swatchManager.initializationPromise);
  new MembershipCartManager();
  
  window.modalManager = new ModalManager();
    
  // Create a direct function for handling video button clicks
  function handleVideoButtonClick(e) {
    e.preventDefault();
    e.stopPropagation(); // Stop any other handlers from executing
        
    // Create video HTML for the modal
    const videoUrl = this.dataset.video;
    const videoHTML = `
      <div class="video-container" style="width: 100%; max-width: 1200px; position: relative; padding-top: 56.25%;">
        <video 
          id="modal-video" 
          controls 
          autoplay 
          style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"
          src="${videoUrl}">
        </video>
      </div>
    `;
    
    // Get modal manager and show modal with video
    if (window.modalManager) {
      window.modalManager.showModal(videoHTML);
    } else {
      console.error('[Video Button] Modal manager not found');
      
      // Fallback: try to use the modal directly
      const modal = document.querySelector(CONFIG.modal.modalSelector);
      if (modal) {
        const modalContent = modal.querySelector(`.${CONFIG.modal.modalContentClass}`);
        if (modalContent) {
          modalContent.innerHTML = videoHTML;
          modal.style.display = 'flex';
          modal.classList.add(CONFIG.modal.activeClass);
          document.body.classList.add('modal-open');
        }
      }
    }
    
    return false; // Prevent default and stop propagation
  }
  
  // Find all video buttons and attach the handler
  const videoButtons = document.querySelectorAll('.button[data-video]');
  
  videoButtons.forEach(button => {
    // Remove any existing click handlers first
    button.removeEventListener('click', handleVideoButtonClick);
    
    // Add our new click handler
    button.addEventListener('click', handleVideoButtonClick);
    
    // Also add the handler directly as an onclick attribute for backup
    button.setAttribute('onclick', 'return false;');
  });
  
});